import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseComponent, FormatService, ReportTableColumn, SummaryItem } from '../../../../../tms-shared';
import {
  SalesOrderBalanceConfirmationReportLine,
  SalesOrderConfirmationBalanceReport,
  SalesOrderDeliveryReport,
  SalesOrderExtraReport,
  SalesOrderVatAnalysis,
} from '../../../../models';
import { SalesOrderReportsService, SalesOrderService } from '../../../../services';

@Component({
  selector: 'app-sales-back-order',
  templateUrl: './sales-back-order.component.html',
  styleUrl: './sales-back-order.component.scss',
})
export class SalesBackOrderComponent extends BaseComponent implements OnInit {
  @Input() salesOrderHeaderId: string;

  model$: Observable<SalesOrderConfirmationBalanceReport>;
  showVatAnalysisOnDocuments: boolean;

  constructor(
    private salesOrderReportsService: SalesOrderReportsService,
    private salesOrderService: SalesOrderService,
    private formatService: FormatService,
    private route: ActivatedRoute,
  ) {
    super();
  }

  ngOnInit() {
    if (!this.salesOrderHeaderId) {
      this.salesOrderHeaderId = this.route.snapshot.paramMap.get('id');
    }
    this.getModel();

    this.salesOrderService.refreshDetails.pipe(takeUntil(this.destroy$)).subscribe(() => this.getModel());
  }

  private getModel() {
    this.model$ = this.salesOrderReportsService.getSalesOrderBalances(this.salesOrderHeaderId);
    this.model$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (model: SalesOrderConfirmationBalanceReport) =>
          (this.showVatAnalysisOnDocuments = model.showVatAnalysisOnDocuments),
      );
  }

  headerSummaryItems(model: SalesOrderConfirmationBalanceReport): SummaryItem[] {
    return [
      { label: $localize`Order Number`, value: model.orderNo },
      { label: $localize`Your Order Number`, value: model.customerOrderNo },
      { label: $localize`Your Order Reference`, value: model.customerOrderRef },
      { label: $localize`Customer Account`, value: model.customerKey },
      { label: $localize`Order Date`, value: this.formatService.date(model.dateOrdered) },
      { label: $localize`Operator`, value: model.enteredBy },
      { label: $localize`Ordered By`, value: model.orderedBy },
    ];
  }

  confirmationSummaryItems(model: SalesOrderConfirmationBalanceReport): SummaryItem[] {
    return model.customerConfirmationEmailAddress
      ? [{ label: $localize`Proforma emailed to`, value: model.customerConfirmationEmailAddress }]
      : [];
  }

  lineColumns(model: SalesOrderBalanceConfirmationReportLine): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: this.formatService.valuesToHtml(this.formatProductDescription(model)),
      },
      {
        label: $localize`Delivery Date`,
        value: this.formatService.date(model.deliveryDate),
        textRight: true,
      },
      {
        label: $localize`To Follow`,
        value: `${this.formatService.number(model.toFollowQty)} ${model.unit}`,
        textRight: true,
      },
      {
        label: $localize`Price`,
        value: `${model.currencySymbol}${this.formatService.number(model.price)} ${model.priceListUnit}`,
        textRight: true,
      },
      {
        label: $localize`Disc %`,
        value: this.formatService.number(model.discount),
        textRight: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
      },
    ];
  }

  deliveryColumns(model: SalesOrderDeliveryReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  extraColumns(model: SalesOrderExtraReport): ReportTableColumn[] {
    return [
      {
        label: $localize`Product`,
        value: model.productDescription,
      },
      {
        label: $localize`Quantity`,
        value: this.formatService.number(model.quantity),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Price`,
        value: this.formatService.currency(model.price, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
      {
        label: $localize`Value`,
        value: this.formatService.currency(model.value, model.currencySymbol),
        textRight: true,
        widthSmall: true,
      },
    ];
  }

  vatAnalysis(model: SalesOrderVatAnalysis): ReportTableColumn[] {
    if (this.showVatAnalysisOnDocuments) {
      return [
        {
          label: '',
          value: '',
        },
        {
          label: $localize`Nett Value`,
          value: this.formatService.currency(model.nettValue, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`VAT Rate`,
          value: this.formatService.number(model.taxRate) + '%',
          textRight: true,
        },
        {
          label: $localize`VAT Value`,
          value: this.formatService.currency(model.vat, model.currencySymbol),
          textRight: true,
        },
        {
          label: $localize`Overall Value`,
          value: this.formatService.currency(model.overallValue, model.currencySymbol),
          textRight: true,
        },
      ];
    } else {
      return [];
    }
  }

  footerSummaryItems(model: SalesOrderConfirmationBalanceReport): SummaryItem[][] {
    if (this.showVatAnalysisOnDocuments) {
      return [
        [
          { label: model.currency ? $localize`Currency` : '', value: model.currency },
          { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
          { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
          { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
          { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
          { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
          { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
          { label: model.documentNotes ? $localize`Notes` : '', value: model.documentNotes },
          {
            label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
            value: model.deliveryInstructions,
          },
        ],
        [],
      ];
    } else {
      return [
        [
          { label: model.currency ? $localize`Currency` : '', value: model.currency },
          { label: model.paymentTerms ? $localize`Payment Terms` : '', value: model.paymentTerms },
          { label: model.deliveryTerms ? $localize`Delivery Terms` : '', value: model.deliveryTerms },
          { label: model.deliveryTermNotes ? $localize`Delivery Term Notes` : '', value: model.deliveryTermNotes },
          { label: model.modeOfTransport ? $localize`Mode of Transport` : '', value: model.modeOfTransport },
          { label: model.shipper ? $localize`Shipper` : '', value: model.shipper },
          { label: model.shippingNotes ? $localize`Shipping Notes` : '', value: model.shippingNotes },
          { label: model.documentNotes ? $localize`Notes` : '', value: model.documentNotes },
          {
            label: model.deliveryInstructions ? $localize`Delivery Instructions` : '',
            value: model.deliveryInstructions,
          },
        ],
        [
          {
            label: $localize`Nett Total`,
            value: this.formatService.currency(model.nettValue, model.currencySymbol),
            textRight: true,
          },
          {
            label: $localize`VAT` + ' @ ' + (model.taxRate > 0 ? ` ${model.taxRate}%` : ''),
            value: this.formatService.currency(model.taxValue, model.currencySymbol),
            textRight: true,
          },
          {
            label: $localize`Overall Total`,
            value: `<strong><u>${this.formatService.currency(model.overallValue, model.currencySymbol)}</u></strong>`,
            textRight: true,
          },
        ],
      ];
    }
  }

  private formatProductDescription(model: SalesOrderBalanceConfirmationReportLine): string[] {
    const values = [model.productKey, model.productDescription];
    if (model.customerProduct) {
      values.push($localize`Customer Product` + ' ' + model.customerProduct);
    }
    if (model.customerLineRef) {
      values.push($localize`Order Reference` + ' ' + model.customerLineRef);
    }
    if (model.makeUp) {
      values.push($localize`Make Up` + ' ' + model.makeUp);
    }
    if (model.contractNumber > 0) {
      values.push(
        $localize`From Contract` +
          ' ' +
          model.contractNumber +
          $localize`Balance remaining` +
          ' ' +
          this.formatService.number(model.contractBalance),
      );
    }

    return values;
  }
}
